import { useMemo, useState } from 'react';
import { Checkbox } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import TextField from '../../Form/TextField/TextField';
import CustomDayPicker from '../../CustomDayPicker/CustomDayPicker';
import ValidationErrorLabel from '../../ValidationErrorLabel/ValidationErrorLabel';
import DeleteDialog from '../DeleteDialog';
import { BinOutlinedIcon } from '../../Icons';

import { getLastDayOFMounth } from '../../../../utils/helpers';
import { deleteModalConfigs } from '../../../../utils/constants';
import { generalEnums } from '../../../../utils/enums';

import classes from '../scss/addEditFormModal.module.scss';

import('react-datepicker/dist/react-datepicker.css');
import('../../../../scss/datepicker.css');

function EducationsForm(props) {
  dayjs.extend(advancedFormat);
  const { addEditForm, withCurrentlyCheckBox, deleteHandler } = props;

  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const deleteModalConfig = useMemo(() => deleteModalConfigs(t), [t]);

  const changeCurrentlyCheckbox = (e) => {
    addEditForm.setFieldValue('enddate', new Date());
    addEditForm.setFieldValue('currentlyStudyHere', e.target.checked);
  };

  return (
    <div className={`${classes.formContainer} w-full`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <TextField
          placeholder="School"
          label="School"
          labelClass="text-sm"
          inputClass="!text-sm px-3 py-4 text-black !font-normal !rounded-lg"
          value={addEditForm.values?.school}
          onChange={(e) => {
            addEditForm.setFieldTouched('school', true, true);
            addEditForm.setFieldValue('school', e.target.value, true);
          }}
          error={addEditForm.touched.school && Boolean(addEditForm.errors.school)}
          errorText={addEditForm.touched.school && addEditForm.errors.school}
        />
        <TextField
          placeholder="Degree"
          label="Degree"
          labelClass="text-sm"
          inputClass="!text-sm px-3 py-4 text-black !font-normal !rounded-lg"
          value={addEditForm.values?.degree}
          onChange={(e) => {
            addEditForm.setFieldTouched('degree', true, true);
            addEditForm.setFieldValue('degree', e.target.value, true);
          }}
          error={addEditForm.touched.degree && Boolean(addEditForm.errors.degree)}
          errorText={addEditForm.touched.degree && addEditForm.errors.degree}
        />
        <TextField
          placeholder="Field of Study"
          label="Field of Study"
          labelClass="text-sm"
          inputClass="!text-sm px-3 py-4 text-black !font-normal !rounded-lg"
          value={addEditForm.values?.fieldofstudy}
          onChange={(e) => {
            addEditForm.setFieldTouched('fieldofstudy', true, true);
            addEditForm.setFieldValue('fieldofstudy', e.target.value, true);
          }}
          error={addEditForm.touched.fieldofstudy && Boolean(addEditForm.errors.fieldofstudy)}
          errorText={addEditForm.touched.fieldofstudy && addEditForm.errors.fieldofstudy}
        />

        {/* Start Date */}
        <div className="flex flex-col gap-2">
          <label className="text-black text-sm">{t('common.forms.startdate')}</label>
          <CustomDayPicker
            isMonthYearPicker
            date={addEditForm.values?.startdate || dayjs()}
            changeHandler={(newValue) => addEditForm.setFieldValue('startdate', newValue, true)}
            dayFormat={generalEnums.DATEWITHSTANDARD}
            sx={{ width: '100%', fieldset: { legend: { width: 'unset' } }, input: { fontSize: '14px' } }}
            rerender
          />
        </div>

        {/* End Date + Checkbox */}
        <div className="flex flex-col gap-2">
          <label className="text-black text-sm">{t('common.forms.enddate')}</label>
          <CustomDayPicker
            isMonthYearPicker
            date={addEditForm.values?.enddate || dayjs()}
            changeHandler={(newValue) => addEditForm.setFieldValue('enddate', getLastDayOFMounth(newValue), true)}
            dayFormat={generalEnums.DATEWITHSTANDARD}
            sx={{ width: '100%', fieldset: { legend: { width: 'unset' } }, input: { fontSize: '14px' } }}
            rerender
          />
          {withCurrentlyCheckBox && (
            <div className={classes.currentCheckBox}>
              <Checkbox
                icon={<div className={classes.customCheck} />}
                checkedIcon={
                  <div className={classes.customCheck}>
                    <DoneIcon />
                  </div>
                }
                onClick={changeCurrentlyCheckbox}
                checked={addEditForm.values?.currentlyStudyHere}
                sx={{ '& .MuiSvgIcon-root': { fontSize: 15 }, padding: 0 }}
              />
              <span className={`${classes.checkBoxLabel} !text-sm`}>I Currently Study Here</span>
            </div>
          )}
        </div>
      </div>

      {addEditForm.touched.currentlyStudyHere && addEditForm.errors.currentlyStudyHere && (
        <ValidationErrorLabel message={addEditForm.errors.currentlyStudyHere} />
      )}

      {/* Delete Button */}
      {!!deleteHandler && (
        <div
          className="mt-4 text-red-600 flex items-center gap-1 cursor-pointer text-sm font-medium"
          role="presentation"
          onClick={() => setOpen(true)}
        >
          <span>{t('common.general.delete')}</span>
          <BinOutlinedIcon />
        </div>
      )}

      <DeleteDialog
        open={open}
        closeHandler={() => setOpen(false)}
        confirmHandler={deleteHandler}
        config={deleteModalConfig.educations}
      />
    </div>
  );
}

export default EducationsForm;
